import React, { useState, useEffect } from 'react';
import { DollarSign, CreditCard, Clock, TrendingUp, ShoppingBag } from 'lucide-react';
import { DashboardStats } from '../../types';
import { apiService } from '../../services/api';
import { getProductCategory } from '../../utils/productCategories';
import { authService } from '../../services/auth';

interface DashboardProps {
  stats: DashboardStats;
  useMockData?: boolean;
}

const Dashboard: React.FC<DashboardProps> = ({ stats, useMockData = true }) => {
  const isAdmin = authService.isAdmin();
  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [weekStats, setWeekStats] = useState({ totalAmount: 0, totalOrders: 0, avgTicket: 0 });
  const [topProducts, setTopProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchDashboardData() {
      setLoading(true);
      try {
        // Fetch last 7 days of transactions from MongoDB
        const now = new Date();
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(now.getDate() - 7);
        sevenDaysAgo.setHours(0, 0, 0, 0);

        const transactions = await apiService.getTransactions({
          startDate: sevenDaysAgo.toISOString(),
          endDate: now.toISOString(),
          limit: 5000,
        });

        // Filter out employee discounts ($0.01 transactions)
        const salesOnly = transactions.filter((txn: any) =>
          !txn.isEmployeeDiscount && txn.amount > 0.10
        );

        // --- Last 7 days stats ---
        const totalAmount = salesOnly.reduce((sum: number, txn: any) => sum + (txn.amount || 0), 0);
        const totalOrders = salesOnly.length;
        setWeekStats({
          totalAmount,
          totalOrders,
          avgTicket: totalOrders > 0 ? totalAmount / totalOrders : 0,
        });

        // --- Last 5 sales (most recent, regardless of day) ---
        const sorted = [...salesOnly].sort(
          (a: any, b: any) =>
            new Date(b.transactionDate || b.createdAt).getTime() -
            new Date(a.transactionDate || a.createdAt).getTime()
        );
        const latest5 = sorted.slice(0, 5).map((txn: any) => ({
          orderNumber: txn.clipId || txn._id,
          orderName: txn.orderName || txn.reference || txn.clipId,
          amount: txn.amount,
          date: txn.transactionDate || txn.createdAt,
          paymentMethod: txn.paymentMethod === 'cash' ? 'cash' : 'card',
          hasDetailedData: txn.hasDetailedData || (txn.products && txn.products.length > 0),
          products: txn.products || [],
        }));
        setRecentOrders(latest5);

        // --- Top products last 7 days ---
        const productMap: Record<string, { quantity: number; revenue: number }> = {};
        for (const txn of salesOnly) {
          if (txn.products && txn.products.length > 0) {
            for (const p of txn.products) {
              const name = p.name || p.producto || '';
              if (!name || name.startsWith('Empleado')) continue;
              if (!productMap[name]) productMap[name] = { quantity: 0, revenue: 0 };
              productMap[name].quantity += p.quantity || 1;
              productMap[name].revenue += p.totalPrice || p.total || (p.unitPrice || p.price || 0) * (p.quantity || 1);
            }
          }
        }
        const topList = Object.entries(productMap)
          .sort(([, a], [, b]) => b.revenue - a.revenue)
          .slice(0, 5)
          .map(([name, data]) => ({
            name,
            quantity: data.quantity,
            revenue: data.revenue,
            category: getProductCategory(name),
          }));
        setTopProducts(topList);
      } catch (err) {
        console.error('Dashboard fetch error:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchDashboardData();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatDateTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
    const time = date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
    return { day, time };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-dark-text font-heading">Panel de Control</h2>
        </div>
        <div className="flex items-center justify-center py-12 text-medium-gray">
          Cargando datos...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-dark-text font-heading">
          Panel de Control
        </h2>
        <div className="text-sm text-medium-gray">
          {new Date().toLocaleDateString('es-MX', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
        </div>
      </div>

      {/* Last 7 Days Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm border border-light-gray/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-medium-gray">
                Ventas (7 dias)
              </p>
              <p className="text-3xl font-bold text-dark-text mt-2">
                {formatCurrency(weekStats.totalAmount)}
              </p>
            </div>
            <div className="bg-green-500 p-3 rounded-lg">
              <DollarSign className="text-white" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-light-gray/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-medium-gray">
                Ordenes (7 dias)
              </p>
              <p className="text-3xl font-bold text-dark-text mt-2">
                {weekStats.totalOrders}
              </p>
            </div>
            <div className="bg-accent p-3 rounded-lg">
              <CreditCard className="text-cream" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm border border-light-gray/20">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-medium-gray">
                Ticket Promedio
              </p>
              <p className="text-3xl font-bold text-dark-text mt-2">
                {formatCurrency(weekStats.avgTicket)}
              </p>
            </div>
            <div className="bg-warm-brown p-3 rounded-lg">
              <TrendingUp className="text-cream" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Latest 5 Sales - Admin Only */}
      {isAdmin && (
        <div className="bg-white rounded-lg p-6 shadow-sm border border-light-gray/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-dark-text font-heading">
              Ultimas 5 Ventas
            </h3>
          </div>
          <div className="space-y-3">
            {recentOrders.length === 0 ? (
              <p className="text-medium-gray text-sm">No hay ventas recientes</p>
            ) : (
              recentOrders.map((order) => {
                const { day, time } = formatDateTime(order.date);
                return (
                  <div
                    key={order.orderNumber}
                    className="flex items-center justify-between p-3 bg-cream/30 rounded-lg hover:bg-cream/50 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
                        {order.paymentMethod === 'cash' ? (
                          <span className="text-lg">💵</span>
                        ) : (
                          <span className="text-lg">💳</span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-dark-text">
                          {order.orderName}
                        </p>
                        <div className="flex items-center space-x-2 text-xs text-medium-gray">
                          <Clock size={12} />
                          <span>{day} {time}</span>
                          <span>·</span>
                          <span>{order.paymentMethod === 'cash' ? 'Efectivo' : 'Tarjeta'}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-dark-text">
                        {formatCurrency(order.amount)}
                      </p>
                      {order.hasDetailedData && (
                        <p className="text-xs text-medium-gray">
                          {order.products.length} productos
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Top Products Last 7 Days - Admin Only */}
      {isAdmin && (
        <div className="bg-white rounded-lg p-6 shadow-sm border border-light-gray/20">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-dark-text font-heading">
              Productos Mas Vendidos (7 dias)
            </h3>
            <ShoppingBag size={20} className="text-medium-gray" />
          </div>
          <div className="space-y-3">
            {topProducts.length === 0 ? (
              <p className="text-medium-gray text-sm">No hay datos de productos</p>
            ) : (
              topProducts.map((product, index) => (
                <div
                  key={product.name}
                  className="flex items-center justify-between p-3 bg-cream/30 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-8 h-8 bg-accent text-cream rounded-lg font-bold text-sm">
                      {index + 1}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-dark-text">
                        {product.name}
                      </p>
                      <p className="text-xs text-medium-gray">
                        {product.category} · {product.quantity} unidades
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-dark-text">
                      {formatCurrency(product.revenue)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
